// CTV-745: the internal (Publica-matching) station name, with search over the
// organization's live Publica channel catalog. Free text stays allowed (the
// name is a PREFIX of the channel names, so "WISC" is a legitimate value that
// matches no catalog entry exactly); the dropdown is there so staff can see
// what Publica actually calls the channels instead of guessing.

import React from 'react';
import { Loader2, Search } from 'lucide-react';
import { inputClass, labelClass } from './publisherFieldStyles';

interface Props {
  value: string;
  onChange: (name: string) => void;
  /** The org's full Publica channel catalog ([] until an org is picked). */
  channels: string[];
  loading?: boolean;
}

const MAX_SUGGESTIONS = 8;

const InternalNameField: React.FC<Props> = ({ value, onChange, channels, loading }) => {
  const [open, setOpen] = React.useState(false);
  const wrapRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const suggestions = React.useMemo(() => {
    const term = value.trim().toLowerCase();
    const pool = term
      ? channels.filter((c) => c.toLowerCase().includes(term))
      : channels;
    // An exact match needs no suggesting.
    return pool.filter((c) => c !== value).slice(0, MAX_SUGGESTIONS);
  }, [channels, value]);

  return (
    <div ref={wrapRef} className="relative">
      <label className={labelClass}>
        Publica name{' '}
        <span className="font-normal normal-case tracking-normal text-slate-400">
          (matches Publica channels by prefix; never shown to advertisers)
        </span>
      </label>
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <input
          required
          value={value}
          role="combobox"
          aria-expanded={open && suggestions.length > 0}
          aria-label="Publica name"
          onChange={(e) => {
            onChange(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          placeholder="WISC"
          className={`${inputClass} pl-9`}
        />
        {loading && (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 animate-spin text-slate-400" />
        )}
      </div>
      {open && suggestions.length > 0 && (
        <div
          role="listbox"
          aria-label="Publica channel suggestions"
          className="absolute z-10 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-52 overflow-y-auto"
        >
          <div className="px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-[0.08em] text-slate-400">
            Publica channels
          </div>
          {suggestions.map((channel) => (
            <button
              key={channel}
              type="button"
              role="option"
              aria-selected={false}
              onClick={() => {
                onChange(channel);
                setOpen(false);
              }}
              className="w-full px-3 py-2 text-left text-sm text-slate-900 hover:bg-slate-50"
            >
              {channel}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default InternalNameField;
